<style lang="scss">
  @use "../../../assets/css/design-system/colors";
  @use "../../../assets/css/design-system/utils";
  .wrapper {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: auto;
  }
</style>

<script>
  import IconButton from "../../atoms/IconButton.svelte";
  import Table from "../Table/Table.svelte";
  import TBODY from "../Table/TBODY.svelte";
  import TD from "../Table/TD.svelte";
  import THEAD from "../Table/THEAD.svelte";
  import TR from "../Table/TR.svelte";
  import DefaultLoading from "./DefaultLoading.svelte";
  import DefaultNoData from "./DefaultNoData.svelte";

  let {
    columns,
    order = $bindable([]),
    data,
    skeleton,
    fetching,
    class: cls = "",
    loadingMessage = "Loading...",
    rowStyle = "",
    cellStyle = "",
    roundedHeader = true,
    includeGap = true,
    skeletonRowCount = 10,
    // Snippets can be referenced here: https://svelte.dev/docs/svelte/snippet#Snippet-scope

    defaultSnippet = _defaultSnippet,
    onMountRow, // Function called in TR onMount
    getRowConfig, // Function that returns all row props (class, handlers, etc)
    ...snippets // snippets
  } = $props();

  const getValue = (row, column) => {
    if (skeleton) return column.skeletonText || "Loading...";
    if (typeof column.data === "string") {
      return row[column.data];
    }
    return column.data(row);
  };

  const getSnippet = (column) =>
    skeleton && column.skeletonText
      ? defaultSnippet
      : snippets[column.name] || defaultSnippet;

  let sortedData = $derived.by(() =>
    skeleton ? Array(skeletonRowCount).map(() => ({})) : data
  );

  const makeOrderClick = (i) => () => {
    const [current] = order || [];
    order = [
      {
        column: i,
        dir: current?.column === i && current?.dir === "asc" ? "desc" : "asc",
      },
    ];
  };

  const sortIcon = (i) => {
    const [current] = order || [];
    if (current?.column === i) {
      return current.dir === "asc"
        ? "fa-duotone fa-solid fa-sort-up"
        : "fa-duotone fa-solid fa-sort-down";
    }
    return "fa-sort fa-solid";
  };
</script>

{#snippet title(column)}
  {column.title}
{/snippet}

{#snippet _defaultSnippet(value)}
  <span class:skeleton>{value}</span>
{/snippet}

{#snippet defaultLoading(message)}
  <DefaultLoading {message} />
{/snippet}

{#snippet defaultNoData()}
  <DefaultNoData />
{/snippet}

<div class="wrapper {cls}">
  {#if fetching || skeleton}
    {@const snip = snippets.loading || defaultLoading}
    {@render snip(loadingMessage)}
  {/if}
  {#if sortedData.length === 0}
    {@const snip = snippets.noData || defaultNoData}
    {@render snip()}
  {:else}
    <Table>
      <THEAD zIndex={sortedData.length + 1} {includeGap}>
        <TR>
          {#each columns as column, i}
            {#if column.visible !== false}
              {@const snip = snippets[`${column.name}Title`] || title}
              {@const { sticky, className = "" } = column}
              <TD
                {sticky}
                class={className}
                {roundedHeader}
                innerStyle={cellStyle}
              >
                {@render snip(column)}
                {#if column.orderable}
                  {@const disabled = skeleton ? "disabled" : ""}
                  {@const icon = sortIcon(i)}
                  <IconButton
                    onclick={makeOrderClick(i)}
                    {icon}
                    disabled={skeleton}
                    class={disabled}
                  />
                {/if}
              </TD>
            {/if}
          {/each}
        </TR>
      </THEAD>
      <TBODY>
        {#if snippets.notification}
          {@const colspan = columns.filter((c) => c.visible !== false).length}
          <TR>
            <TD {colspan}>
              {@render snippets.notification()}
            </TD>
          </TR>
        {/if}
        {#each sortedData as row, rowIndex}
          {@const zIndex = sortedData.length - rowIndex}
          {@const onMountCallback = onMountRow ? () => onMountRow(row) : null}
          {@const rowConfig = getRowConfig ? getRowConfig(row) : {}}
          <TR {onMountCallback} style={rowStyle} {...rowConfig}>
            {#each columns as column}
              {#if column.visible !== false}
                {@const { sticky, className = "" } = column}
                <TD {sticky} class={className} {zIndex} innerStyle={cellStyle}>
                  {@const value = getValue(row, column)}
                  {@const snip = getSnippet(column)}
                  {@render snip(value, row, column)}
                </TD>
              {/if}
            {/each}
          </TR>
        {/each}
      </TBODY>
    </Table>
  {/if}
</div>
